import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { logErrorToSentry } from '../lib/log-error-to-sentry';
import { ErrorFallback } from './error-fallback';

const isClerkError = (error: unknown): boolean => {
	// Check if this is a Clerk loading error
	if (
		typeof error === 'string' &&
		error.includes('Clerk: Failed to load Clerk')
	) {
		return true;
	}

	// Check if it's an Error object with Clerk message
	if (
		error instanceof Error &&
		error.message.includes('Clerk: Failed to load Clerk')
	) {
		return true;
	}

	return false;
};

const ClerkErrorFallback = ({ xid }: { xid: string }) => {
	const { t } = useTranslation();

	return (
		<ErrorFallback
			xid={xid}
			icon={faCircleExclamation}
			title={t('errors.clerk.title', 'Authentication Service Unavailable')}
			message={t(
				'errors.clerk.message',
				"We're having trouble connecting to our authentication service. This might be due to network issues or browser extensions blocking the connection.",
			)}
			troubleshootingHeader={t(
				'errors.if-problem-persists',
				'If the problem persists:',
			)}
			troubleshootingTips={[
				t(
					'errors.clerk.troubleshooting.disable-ad-blockers',
					'Disable ad blockers or browser extensions',
				),
				t(
					'errors.clerk.troubleshooting.try-different-browser',
					'Try a different browser',
				),
				t(
					'errors.clerk.troubleshooting.clear-cache',
					'Clear your browser cache and cookies',
				),
			]}
			errorReferenceLabel={t('errors.error-reference', 'Error Reference')}
			errorReferenceDescription={t(
				'errors.provide-code-when-reporting',
				'Please provide this code when reporting the issue',
			)}
		/>
	);
};

export const ClerkErrorBoundary = ({ children }: { children: ReactNode }) => {
	return (
		<ErrorBoundary
			fallbackRender={(props) => {
				// Only show our custom error UI for Clerk errors
				if (isClerkError(props.error)) {
					// Log to Sentry with XID
					const xid = logErrorToSentry({
						error: props.error,
						context: 'ClerkErrorBoundary',
						level: 'warning',
						extraTags: {
							error_type: 'clerk_loading_failure',
							userAgent: navigator.userAgent,
							online: String(navigator.onLine),
						},
					});

					return <ClerkErrorFallback xid={xid} />;
				}
				// Re-throw non-Clerk errors to be handled by parent error boundaries
				throw props.error;
			}}
			onReset={() => {
				// For Clerk loading errors, we need a full page reload
				window.location.reload();
			}}
		>
			{children}
		</ErrorBoundary>
	);
};
